<template>
  <div
    v-if="state.enabled"
    class="viewport-debug"
    :class="{ 'viewport-debug--collapsed': state.collapsed }"
  >
    <div class="viewport-debug__head">
      <strong @click="state.collapsed = !state.collapsed">
        viewport debug
        <template v-if="state.collapsed">
          · {{ state.innerHeight }} / {{ state.syncAppliedHeight || '—' }}
          <template v-if="state.syncCollapsed"> · COLLAPSED</template>
        </template>
      </strong>
      <button
        type="button"
        @click="state.collapsed = !state.collapsed"
      >
        {{ state.collapsed ? '▲' : '▼' }}
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="refresh"
      >
        refresh
      </button>
      <button
        v-if="!state.collapsed"
        type="button"
        @click="disable"
      >
        off
      </button>
    </div>
    <pre v-if="!state.collapsed">{{ text }}</pre>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive } from 'vue'
import { pwaUtils } from '@/utils/pwaUtils.js'
import { getDocumentHeightDebugInfo } from '@/utils/syncDocumentHeight.js'

/** localStorage: переживает reload после оплаты; обычные юзеры флаг не ставят */
const STORAGE_KEY = 'debugViewport'
const SECRET_TAPS = 7
/** Все 7 тапов должны уложиться в это окно от первого тапа серии */
const SECRET_TAP_WINDOW_MS = 10_000
const SECRET_CORNER_SIZE = 72

const state = reactive({
  enabled: false,
  collapsed: false,
  updatedAt: '',
  innerHeight: 0,
  innerWidth: 0,
  clientHeight: 0,
  clientWidth: 0,
  offsetHeight: 0,
  screenHeight: 0,
  screenAvailHeight: 0,
  vvHeight: 0,
  vvWidth: 0,
  vvOffsetTop: 0,
  vvOffsetLeft: 0,
  vvScale: 0,
  documentHeightVar: '',
  documentOffsetBottom: '',
  bodyClientHeight: 0,
  standalone: false,
  displayMode: '',
  visibility: '',
  navHeight: 0,
  gapBelowNav: 0,
  syncPrimed: false,
  syncBaselineInner: 0,
  syncBaselineChrome: 0,
  syncStoredInner: 0,
  syncStoredChrome: 0,
  syncCollapsed: false,
  syncInputFocused: false,
  syncAppliedHeight: 0,
})

const text = computed(() => {
  const lines = [
    `t: ${state.updatedAt}`,
    `standalone: ${state.standalone}`,
    `display-mode: ${state.displayMode}`,
    `visibility: ${state.visibility}`,
    `inner: ${state.innerWidth}×${state.innerHeight}`,
    `client(html): ${state.clientWidth}×${state.clientHeight}`,
    `offset(html): ${state.offsetHeight}`,
    `body.clientH: ${state.bodyClientHeight}`,
    `screen: ${state.screenHeight} / avail ${state.screenAvailHeight}`,
    `vv: ${state.vvWidth}×${state.vvHeight}`,
    `vv.offsetTop: ${state.vvOffsetTop}`,
    `vv.offsetLeft: ${state.vvOffsetLeft}`,
    `vv.scale: ${state.vvScale}`,
    `--document-height: ${state.documentHeightVar}`,
    `--document-offset-bottom: ${state.documentOffsetBottom}`,
    `nav.h: ${state.navHeight}`,
    `gap below nav: ${state.gapBelowNav}`,
    `inner - vv.h - vv.top: ${state.innerHeight - state.vvHeight - state.vvOffsetTop}`,
    `screen - inner: ${state.screenHeight - state.innerHeight}`,
    `--- sync ---`,
    `sync.primed: ${state.syncPrimed}`,
    `sync.baselineInner: ${state.syncBaselineInner}`,
    `sync.baselineChrome: ${state.syncBaselineChrome}`,
    `sync.storedInner: ${state.syncStoredInner}`,
    `sync.storedChrome: ${state.syncStoredChrome}`,
    `sync.collapsed: ${state.syncCollapsed}`,
    `sync.inputFocused: ${state.syncInputFocused}`,
    `sync.appliedHeight: ${state.syncAppliedHeight}`,
    `---`,
    `off: кнопка off / 7 тапов впр.низу за 10с`,
  ]

  return lines.join('\n')
})

function readEnabled() {
  const params = new URLSearchParams(window.location.search)

  if (params.get('debugViewport') === '1') {
    localStorage.setItem(STORAGE_KEY, '1')
    return true
  }

  if (params.get('debugViewport') === '0') {
    localStorage.removeItem(STORAGE_KEY)
    return false
  }

  return localStorage.getItem(STORAGE_KEY) === '1'
}

function setEnabled(value) {
  state.enabled = value

  if (value) {
    localStorage.setItem(STORAGE_KEY, '1')
    refresh()
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
}

function disable() {
  setEnabled(false)
}

function getDisplayMode() {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return 'standalone'
  }

  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return 'minimal-ui'
  }

  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return 'fullscreen'
  }

  if (window.matchMedia('(display-mode: browser)').matches) {
    return 'browser'
  }

  return navigator.standalone ? 'navigator.standalone' : 'unknown'
}

function measureNavGap() {
  const nav = document.querySelector('.navigation')

  if (!nav) {
    state.navHeight = 0
    state.gapBelowNav = 0
    return
  }

  const rect = nav.getBoundingClientRect()
  state.navHeight = Math.round(rect.height)
  state.gapBelowNav = Math.round(window.innerHeight - rect.bottom)
}

function refresh() {
  const root = document.documentElement
  const styles = getComputedStyle(root)
  const vv = window.visualViewport

  state.updatedAt = new Date().toLocaleTimeString('ru-RU')
  state.innerHeight = window.innerHeight
  state.innerWidth = window.innerWidth
  state.clientHeight = root.clientHeight
  state.clientWidth = root.clientWidth
  state.offsetHeight = root.offsetHeight
  state.bodyClientHeight = document.body?.clientHeight ?? 0
  state.screenHeight = window.screen?.height ?? 0
  state.screenAvailHeight = window.screen?.availHeight ?? 0
  state.vvHeight = Math.round(vv?.height ?? 0)
  state.vvWidth = Math.round(vv?.width ?? 0)
  state.vvOffsetTop = Math.round(vv?.offsetTop ?? 0)
  state.vvOffsetLeft = Math.round(vv?.offsetLeft ?? 0)
  state.vvScale = Number((vv?.scale ?? 1).toFixed(3))
  state.documentHeightVar = styles.getPropertyValue('--document-height').trim()
  state.documentOffsetBottom = styles.getPropertyValue('--document-offset-bottom').trim() || '(empty)'
  state.standalone = pwaUtils.isStandalone()
  state.displayMode = getDisplayMode()
  state.visibility = document.visibilityState
  measureNavGap()

  const sync = getDocumentHeightDebugInfo()
  state.syncPrimed = sync.primed
  state.syncBaselineInner = sync.baselineInnerHeight
  state.syncBaselineChrome = sync.baselineChromeGap
  state.syncStoredInner = sync.storedInner
  state.syncStoredChrome = sync.storedChrome
  state.syncCollapsed = sync.collapsed
  state.syncInputFocused = !!sync.inputFocused
  state.syncAppliedHeight = Math.round(sync.appliedHeight)
}

let stop = null
/** timestamps последних тапов в секретный угол (мс) */
let secretTapTimes = []

function isSecretCornerTap(clientX, clientY) {
  const fromRight = window.innerWidth - clientX <= SECRET_CORNER_SIZE
  const fromBottom = window.innerHeight - clientY <= SECRET_CORNER_SIZE
  return fromRight && fromBottom
}

function onSecretPointer(event) {
  let clientX
  let clientY

  if (event.changedTouches?.[0]) {
    clientX = event.changedTouches[0].clientX
    clientY = event.changedTouches[0].clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }

  if (!isSecretCornerTap(clientX, clientY)) {
    return
  }

  const now = Date.now()
  // Оставляем только тапы внутри окна 10с от «сейчас»
  secretTapTimes = secretTapTimes.filter(t => now - t <= SECRET_TAP_WINDOW_MS)
  secretTapTimes.push(now)

  if (secretTapTimes.length >= SECRET_TAPS) {
    secretTapTimes = []
    setEnabled(!state.enabled)
  }
}

onMounted(() => {
  state.enabled = readEnabled()

  if (state.enabled) {
    refresh()
  }

  const onResume = () => {
    if (!state.enabled) {
      return
    }

    refresh()
    requestAnimationFrame(refresh)
    setTimeout(refresh, 50)
    setTimeout(refresh, 300)
    setTimeout(refresh, 1000)
  }

  window.addEventListener('resize', refresh)
  window.visualViewport?.addEventListener('resize', refresh)
  window.visualViewport?.addEventListener('scroll', refresh)
  window.addEventListener('orientationchange', onResume)
  window.addEventListener('pageshow', onResume)
  window.addEventListener('focus', onResume)
  document.addEventListener('visibilitychange', onResume)

  // Скрытый вход: 7 быстрых тапов в правый нижний угол (зона «Профиль»)
  const secretEvent = 'ontouchstart' in window ? 'touchend' : 'click'
  document.addEventListener(secretEvent, onSecretPointer, { passive: true })

  const intervalId = window.setInterval(() => {
    if (state.enabled) {
      refresh()
    }
  }, 1000)

  stop = () => {
    window.removeEventListener('resize', refresh)
    window.visualViewport?.removeEventListener('resize', refresh)
    window.visualViewport?.removeEventListener('scroll', refresh)
    window.removeEventListener('orientationchange', onResume)
    window.removeEventListener('pageshow', onResume)
    window.removeEventListener('focus', onResume)
    document.removeEventListener('visibilitychange', onResume)
    document.removeEventListener(secretEvent, onSecretPointer)
    window.clearInterval(intervalId)
  }
})

onUnmounted(() => {
  stop?.()
})
</script>

<style scoped>
.viewport-debug {
  position: fixed;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 99999;
  max-height: 45vh;
  overflow: auto;
  padding: 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.82);
  color: #9ef01a;
  font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  pointer-events: auto;
  -webkit-overflow-scrolling: touch;
}

.viewport-debug--collapsed {
  right: auto;
  max-height: none;
  overflow: visible;
}

.viewport-debug__head {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
  color: #fff;
}

.viewport-debug--collapsed .viewport-debug__head {
  margin-bottom: 0;
}

.viewport-debug__head strong {
  cursor: pointer;
  user-select: none;
}

.viewport-debug__head button {
  padding: 2px 8px;
  border-radius: 4px;
  background: #333;
  color: #fff;
  font-size: 11px;
}

.viewport-debug pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
